import React, { useEffect, useMemo, useState } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import style from 'assets/jss/material-dashboard-pro-react/views/services/FixedBusinessBroadband/selectPlanDialogStyle';
import { TableContainer, Table, TableBody, TableHead, TableRow, makeStyles, TableCell } from '@material-ui/core';
import { setToast } from 'redux/modules/toast/actions';
import { useDispatch, useSelector } from 'react-redux';
import { ProductsService } from 'services/products/products';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { CustomAttribute, extractAttributeValue } from 'utils/ecommerce/services/extract';
import { catchError, finalize } from 'rxjs/operators';
import invalidateCache from 'utils/api/invalidateCache';
import { SingleCustomerProduct } from 'utils/api/models/products/singleCustomerProduct';
import SimpleTabs from 'components/SimpleTabs/SimpleTabs';
import { AccountType, BillingType } from 'utils/api/models/accounts/singleAccount';
import {
	customizePlanName,
	hiddenPlans,
	customizedPlansFilters,
	productFilters as productNames,
} from 'components/ServicesTables/PostpaidLinesTable/constants';
import { CommitmentDetails } from 'utils/api/models/payments/commitmentDetails';
import InfoClause from 'components/infoClause/infoClause';
import { getLanguage } from 'utils/language/language';
import {
	contactNumberSelector,
	emailSelector,
	userContactNumberSelector,
	userNameSelector,
} from 'redux/modules/session/selectors';
import { BodyL, BodyMBold, BodySBold } from 'components/Typography/Typography';
import {
	getCommonLabels,
	getEmailAddress,
	getFiberProLabels,
	getTemplateKeys,
} from 'redux/modules/dynamicLabels/selectors';
import { SingleCommitmentDetails } from 'utils/api/models/payments/singleCommitmentDetails';
import { SubscriberDetails } from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import { numberTypeCopper } from 'views/Numbers/constants';
import LocationTab from 'views/AddNewServices/AddSingleService/tabs/LocationTab';
import * as omantelMicroApi from 'utils/api/payloads/omantelMicroApi';
import { copperPlan, fiberPro2b, wfbb } from 'utils/killswitch';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { throwError } from 'rxjs';
import VatIcon from 'assets/img/icon_vat_grey.svg';
import { FAILURE } from '../../common/BandwidthOnDemand/constants';
import StatusDialog from 'components/StatusDialog/StatusDialog';
import { loadQualtrics, setQualtricsEmbeddedData } from 'utils/qualtrics';
import OtpDialog from 'views/Auth/common/OtpDialog';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { UsersService } from 'services/users/users';
import { OperationContextEnum } from 'utils/api/payloads/omantelApi';
import { ERROR_VALIDATING_TOKEN } from 'utils/api/errors/errorCodes';

interface IChangePlanDialogProps {
	hideDialog: () => void;
	TableComponent: React.ElementType;
	planTitle: string;
	partNumber?: string;
	phoneNumber: string;
	accountType: string;
	displaySuccessDialog: (refNo?: string) => void;
	setChangePlanStatus: (status: boolean) => void;
	setChangePlanErrorType: (type: string) => void;
	reloadCustomerProducts: () => void;
	currentPlanId?: string;
	corpFbbCommitment: SingleCustomerProduct | undefined;
	isOpen: boolean;
	accountNumber: string;
	userName?: string;
	commitmentDetails: CommitmentDetails[];
	activeCommitMents?: SingleCommitmentDetails;
	planTypeDetails?: SubscriberDetails;
	removeProductIds?: string[];
	tarrifPlanId?: string;
	isActivePlanACustomizedPlan?: boolean;
	productFilters?: string[] | null;
	hiddenPlans?: string[];
}
interface PlanName {
	name: string;
	isPlanFiberPro?: Boolean;
}
export interface ChangePlanDetails {
	oldPlan: PlanName;
	newPlan: PlanName;
}
// @ts-ignore
const useStyles = makeStyles(style);

// eslint-disable-next-line react/prop-types
const ChangePlanDialog = ({
	hideDialog,
	partNumber,
	TableComponent,
	planTitle,
	phoneNumber,
	accountType,
	displaySuccessDialog,
	reloadCustomerProducts,
	setChangePlanStatus,
	setChangePlanErrorType,
	currentPlanId,
	corpFbbCommitment,
	isOpen,
	accountNumber,
	commitmentDetails,
	userName,
	activeCommitMents,
	planTypeDetails,
	removeProductIds,
	tarrifPlanId,
	isActivePlanACustomizedPlan,
	productFilters = [],
	hiddenPlans = [],
}: IChangePlanDialogProps) => {
	const [address, setAddress] = useState<{ [key: string]: string }>({});
	const [location, setLocation] = useState<{ [key: string]: string }>({});
	const [gis, setGis] = useState<string | undefined>('');
	const [nextStepDisabled, setNextStepDisabled] = useState<boolean>(true);
	const [selectedPlan, setSelectedPlan] = useState<types.ecommerce.CatalogEntryView | null>(null);
	const [changePlansDetails, setChangePlanDetails] = useState<ChangePlanDetails | null>({
		oldPlan: { name: activeCommitMents?.name || '' },
		newPlan: { name: '' },
	});
	const [fiberProLocationError, setFiberProLocationError] = useState<boolean>(false); // state for  fiber pro location error
	const [fiberProLocationavailable, setFiberProLocationavailable] = useState<boolean>(true); // state for  fiber pro location error dialog handling
	const [selectedBoosterPlan, setSelectedBoosterPlan] = useState<string | null>(null);
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false);
	const classes = useStyles();
	const dispatch = useDispatch();
	const [isChangingTariff, setChangingTariff] = useState<boolean>(false);
	const [changePlanSteps, setChangePlanSteps] = useState<number>(1);
	const [selectedTariff, setSelectedTariff] = useState<any>();
	const [selectedCommitment, setSelectedCommitment] = useState<any>();
	const [activeTab, setActiveTab] = useState(0);
	const [commitmentDuration, setCommitmentDuration] = useState<string | null>(null);
	const [commitment, setCommitment] = useState<number | null>(commitmentDuration ? +commitmentDuration : null);
	const [scrollToButton, setScrollToButton] = useState<boolean>(false);
	const [loading, setLoading] = useState(false);
	const [isOTPStep, setIsOtpStep] = useState(false);
	const [otp, setOtp] = useState('');
	const [isWrongOtp, setIsWrongOtp] = useState(false);
	const [loadingPlans, setLoadingPlans] = useState(true);
	const [showNama2022Tab, setShowNama2022Tab] = useState(false);
	const language = getLanguage();
	const email = useSelector(emailSelector);
	const loggedInUserName = useSelector(userNameSelector);
	const contactNumber = useSelector(contactNumberSelector);
	const userNumber = useSelector(userContactNumberSelector);
	const fiberProLabels = useSelector(getFiberProLabels);
	const commonLabels = useSelector(getCommonLabels);
	const templateKeys = useSelector(getTemplateKeys);
	const emailAddress = useSelector(getEmailAddress);
	const remoteConfig = useSelector(remoteConfigSelector);
	const isFiberPro2b = fiberPro2b(remoteConfig); // kill switch to show fiber pro 2b changes
	const isWfbb = wfbb(remoteConfig); // kill switch wfbb
	const isCopperPlan = copperPlan(remoteConfig); // kill switch copper plan changes

	const tabs = useMemo(() => {
		const filteredProducts = productFilters?.filter((pf) =>
			isActivePlanACustomizedPlan ? customizedPlansFilters.includes(pf) : !hiddenPlans.includes(pf)
		);
		return filteredProducts?.map((pf) => {
			return {
				name: pf,
				content: (
					<TableComponent
						selectedTab={selectedPlan?.uniqueID || null}
						setSelectedTab={(plan: types.ecommerce.CatalogEntryView | null) => {
							plan ? setSelectedPlan(plan) : setSelectedPlan(null);
						}}
						currentPlanId={currentPlanId}
						currentCommitmentId={corpFbbCommitment?.productId}
						accountId={accountNumber}
						setSelectedTariff={setSelectedTariff}
						setSelectedCommitment={setSelectedCommitment}
						selectedProductFilter={pf}
						setCommitmentDuration={setCommitmentDuration}
						commitmentDuration={commitmentDuration}
						commitment={commitment}
						setCommitment={setCommitment}
					/>
				),
			};
		});
	}, [
		TableComponent,
		accountNumber,
		commitment,
		commitmentDetails,
		commitmentDuration,
		corpFbbCommitment,
		currentPlanId,
		isActivePlanACustomizedPlan,
		productFilters,
		selectedPlan,
		setCommitment,
		setCommitmentDuration,
		setSelectedCommitment,
		setSelectedTariff,
	]);
	useEffect(() => {
		if (changePlanSteps === 1 && gis && gis.length > 0) {
			const { OLT, FDH, MDU, AP, OBCTAGNUMBER, OBCOLT, OBCFDHNUMBER } = address;
			// check that fiber pro is available for that location
			if (!((OLT && FDH && MDU && AP) || (OBCTAGNUMBER && OBCOLT && OBCFDHNUMBER))) {
				setFiberProLocationError(true);
				setFiberProLocationavailable(false);
			} else {
				setFiberProLocationavailable(true);
				setScrollToButton(true);
			}
		}
	}, [gis, address]);
	const changeFixPlan = () => {
		if (!!selectedPlan && !!partNumber) {
			setChangingTariff(true);
			ProductsService.changeFixedPlan(selectedPlan.partNumber, partNumber, phoneNumber, accountType).subscribe(
				(r) => {
					setChangingTariff(false);
					if (r.success) {
						reloadCustomerProducts();
						displaySuccessDialog();
						setChangePlanStatus(true);
					} else {
						dispatch(
							setToast({
								error: true,
								label: translate('error.wrong.response'),
							})
						);
						setChangePlanStatus(false);
						hideDialog();
						loadQualtrics();
					}
				},
				() => {
					dispatch(
						setToast({
							error: true,
							label: translate('error.wrong.response'),
						})
					);
					setChangingTariff(false);
					hideDialog();
					loadQualtrics();
				}
			);
		}
	};

	const showFailureToast = () => {
		dispatch(
			setToast({
				error: true,
				label: translate('error.wrong.response'),
			})
		);
		loadQualtrics();
	};

	const changeInternetPlan = () => {
		// if copper plan feature flag is active and if it is a copper plan then user need to select location
		const getWfbbData = (changePlanPayload: omantelMicroApi.ChangeFbbOrder) => {
			if (isWfbb) {
				changePlanPayload = {
					...changePlanPayload,
					// set sales leads
					salesLeads: [
						{
							productId: extractAttributeValue(selectedPlan, CustomAttribute.WFBB),
							name: selectedPlan?.attributes.find(
								(attribute) => attribute.identifier === CustomAttribute.WFBB
							)?.name,
							emailAddress: emailAddress.wfbb,
							templateKey: templateKeys.wfbbChange, // wfbb_direct_contact_body / wfbb_direct_contact_subject
						},
					],
				};
			}
			return changePlanPayload;
		};
		const getCopperData = (changePlanPayload: omantelMicroApi.ChangeFbbOrder) => {
			if (isCopperPlan) {
				changePlanPayload = {
					...changePlanPayload,
					copperPlan: planTypeDetails?.subServiceType === numberTypeCopper,
					copperPlanDetails: {
						commitmentDetails: {
							productId: activeCommitMents?.productId,
							productName: activeCommitMents?.name,
						},
						removeProductIds: removeProductIds,
						gisDetails: gis ?? '',
					},
				};
			}
			return changePlanPayload;
		};
		if ((!!selectedPlan || !!selectedBoosterPlan) && !!partNumber) {
			setChangingTariff(true);
			let changePlanPayload: omantelMicroApi.ChangeFbbOrder = {
				subscriberNo: phoneNumber,
				userFullName: loggedInUserName,
				currentProductId: activeCommitMents?.productId || partNumber,
				billingAccountNumber: accountNumber,
				newProductId: selectedBoosterPlan ? selectedBoosterPlan : selectedPlan ? selectedPlan.partNumber : '',
				emailAddress: email,
				mobileNumber: contactNumber,
				otp,
			};
			// if old plan is not fiber pro and new plan is fiber pro that saleslead details will go and fiberPro2b kill swith is on
			if (
				!changePlansDetails?.oldPlan.isPlanFiberPro &&
				changePlansDetails?.newPlan.isPlanFiberPro &&
				isFiberPro2b
			) {
				changePlanPayload = {
					...changePlanPayload,
					templateKey: templateKeys.changePlan,
				};
				// get the wfbb and copper plan data based on the feature flag
				changePlanPayload = getWfbbData(changePlanPayload);
			}
			changePlanPayload = getCopperData(changePlanPayload);

			if (changePlansDetails) {
				setLoading(true);
				ProductsService.changeFbbOrder(changePlanPayload)
					.pipe(
						catchError((error) => {
							if (error?.status === 500) {
								let errors = error?.response?.errors || [];
								setChangePlanErrorType(errors && errors.length > 0 ? errors[0].code : '');
							}
							return throwError(error);
						}),
						finalize(() => setChangingTariff(false))
					)
					.subscribe(
						(res) => {
							setLoading(false);
							setIsOtpStep(false);
							setConfirmationDialogOpen(false);
							reloadCustomerProducts();
							if (res?.response?.data?.orderId) {
								invalidateCache(['getCommitmentDetails', 'getCustomerProducts'], true);
								hideDialog();
								displaySuccessDialog();
								setChangePlanStatus(true);
							} else {
								hideDialog();
								displaySuccessDialog();
								setChangePlanStatus(false);
							}
						},
						(error) => {
							setLoading(false);
							if (error?.response?.errors[0]?.code === ERROR_VALIDATING_TOKEN) {
								setIsWrongOtp(true);
								return;
							}
							setIsOtpStep(false);
							setConfirmationDialogOpen(false);
							hideDialog();
							displaySuccessDialog();
							setChangePlanStatus(false);
						}
					);
			}
		}
	};

	const changeMobilePlan = () => {
		if (!!selectedTariff && currentPlanId) {
			setChangingTariff(true);
			const orderItems: types.services.IServiceItem[] = [
				{
					action: 'ADD',
					productIds: [selectedTariff?.productId, selectedCommitment?.productId],
				},
				{
					action: 'REMOVE',
					productIds: [currentPlanId],
				},
			];
			ProductsService.submitServices({
				phoneNumber,
				orderItems,
				ebuConfigProduct: 'MOBILE_PLAN_CHANGE',
			})
				.pipe(finalize(() => setChangingTariff(false)))
				.subscribe(
					(response) => {
						if (response.returnCode === '0') {
							reloadCustomerProducts();
							hideDialog();
							displaySuccessDialog(response.referenceId);
							setChangePlanStatus(true);
						} else {
							hideDialog();
							showFailureToast();
							setChangePlanStatus(false);
						}
					},
					() => {
						hideDialog();
						showFailureToast();
					}
				);
		}
	};

	// Checks if the plan change flow is at step 1 and Plan is a Copper plan with Internet account type.
	const isCopperPlanChangeStep =
		changePlanSteps === 1 &&
		planTypeDetails?.subServiceType === numberTypeCopper &&
		isCopperPlan &&
		accountType === AccountType.INTERNET;
	const changeFbbPlan = () => {
		if (isCopperPlanChangeStep) {
			setChangePlanSteps(2);
		} else {
			setConfirmationDialogOpen(true);
		}
	};

	const changePlan = () => {
		switch (accountType) {
			case AccountType.FIX:
				changeFixPlan();
				setQualtricsEmbeddedData({ activity_type: 'Plan Upgrade - PSTN' });
				break;
			case AccountType.INTERNET:
				changeFbbPlan();
				setQualtricsEmbeddedData({ activity_type: 'Plan Upgrade - Fiber Pro' });
				break;
			case AccountType.MOBILE:
				changeMobilePlan();
				setQualtricsEmbeddedData({ activity_type: 'Plan Upgrade - Mobile' });
				break;

			default:
				break;
		}
	};

	const requestForOtp = () => {
		dispatch(showLoadingIndicator());
		UsersService.requestForOtp(OperationContextEnum.CHANGEFBBPLAN, userNumber)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(
				() => {
					setToast({
						error: false,
						label: translate('otp.token.sent'),
					});
					setIsOtpStep(true);
				},
				(error) => {
					if (error.response.code === ERROR_VALIDATING_TOKEN) {
						setIsWrongOtp(true);
					}
				}
			);
	};

	const handleSetActiveTab = (e: React.SetStateAction<number>) => {
		setSelectedCommitment(null);
		setCommitmentDuration(null);
		setCommitment(null);
		setActiveTab(e);
	};
	return (
		<CustomDialog
			isOpen={isOpen}
			width={938}
			hideDialog={() => !isChangingTariff && hideDialog()}
			header={isCopperPlanChangeStep ? fiberProLabels.copperPlanLoc : fiberProLabels.editYourPlan}
			setScrollToButton={setScrollToButton}
			scrollToButton={scrollToButton}
			rightSideButtons={[
				{
					text: commonLabels.cancel,
					color: 'tertiary-light',
					onClick: () => {
						hideDialog();
					},
					disabled: isChangingTariff,
				},
				{
					text: fiberProLabels.changePlan,
					disabled: isCopperPlanChangeStep
						? !(gis && gis.length > 0 && fiberProLocationavailable)
						: !selectedTariff &&
						  !selectedBoosterPlan &&
						  (!selectedPlan || selectedPlan.partNumber === partNumber || isChangingTariff),
					buttonProps: {},
					ref: true,
					color: 'primary-light',
					id: 'changePlanButton',
					onClick: changePlan,
				},
			]}
			dialogBody={
				<ConditionalRender
					show={
						changePlanSteps === 1 &&
						planTypeDetails?.subServiceType === numberTypeCopper &&
						isCopperPlan &&
						accountType === AccountType.INTERNET
					}
					onTrue={() => (
						<>
							<LocationTab
								setNextStepDisabled={setNextStepDisabled}
								setGis={setGis}
								setAddress={setAddress}
								location={location}
								setLocation={setLocation}
								setLocationInputs={(name: string, value: string) =>
									setLocation((location) => ({
										...location,
										[name]: value,
									}))
								}
								address={address}
								height={'760px'}
							/>
							<StatusDialog
								isModalOpen={fiberProLocationError}
								closeModal={() => {
									setFiberProLocationavailable(false);
									setFiberProLocationError(false);
								}}
								heading={''}
								desc2=""
								desc={fiberProLabels.fbbProNotAvailable}
								status={FAILURE}
								footer=""
							/>
						</>
					)}
					onFalse={() => (
						<>
							<ConditionalRender
								show={loading}
								onTrue={() => <CustomCircularProgress loading text="" transparent />}
								onFalse={() => (
									<div className={classes.dialogBodyWrapper}>
										<ConditionalRender
											show={isChangingTariff}
											onTrue={() => (
												<CustomCircularProgress
													text={
														isChangingTariff
															? commonLabels.upgradeReqLoading
															: commonLabels.pleaseWait
													}
													loading
													transparent
												/>
											)}
											onFalse={() => (
												<>
													{accountType === AccountType.MOBILE ? (
														<>
															{tabs && (
																<SimpleTabs
																	tabs={tabs}
																	activeTab={activeTab}
																	setActiveTab={handleSetActiveTab}
																/>
															)}
														</>
													) : (
														<TableComponent
															selectedTab={selectedPlan?.uniqueID || null}
															selectedBoosterPlan={selectedBoosterPlan}
															setSelectedBoosterPlan={setSelectedBoosterPlan}
															setSelectedTab={(
																plan: types.ecommerce.CatalogEntryView | null
															) => {
																setSelectedBoosterPlan(null);
																plan ? setSelectedPlan(plan) : setSelectedPlan(null);
															}}
															subscriberNo={phoneNumber}
															currentPlanId={currentPlanId}
															setChangePlanDetails={setChangePlanDetails}
															changePlansDetails={changePlansDetails}
															currentCommitmentId={activeCommitMents?.productId}
															accountId={accountNumber}
															setSelectedTariff={setSelectedTariff}
															setSelectedCommitment={setSelectedCommitment}
															planTypeDetails={planTypeDetails}
															setCommitmentDuration={setCommitmentDuration}
															tarrifPlanId={tarrifPlanId}
															loadingPlans={setLoadingPlans}
														/>
													)}
													{fiberProLabels?.vatBanner && !loadingPlans && (
														<InfoClause
															message={fiberProLabels.vatBanner}
															Icon={<img alt="vat icon" src={VatIcon} />}
														/>
													)}
												</>
											)}
										/>
									</div>
								)}
							/>
							<CustomDialog
								header={fiberProLabels.changePlanHeading}
								rightSideButtons={[
									{
										text: commonLabels.no,
										color: 'tertiary-light',
										onClick: () => {
											setConfirmationDialogOpen(false);
										},
									},
									{
										text: commonLabels.yes,
										color: 'primary-light',
										onClick: requestForOtp,
									},
								]}
								isOpen={confirmationDialogOpen}
								width={changePlanSteps === 1 ? 670 : 1000}
								dialogBody={
									<div className={classes.confirmationDialogBody}>
										<div className={classes.confirmationDialogBody}>
											<BodyL>{fiberProLabels.changePlanSubHeading}</BodyL>

											<TableContainer className={classes.confirmationDialogTableWrapper}>
												<Table>
													<TableHead className={classes.tableHead}>
														<TableRow>
															<TableCell>
																<BodyMBold>{fiberProLabels.yourNewPlan}</BodyMBold>
															</TableCell>
															<TableCell>
																<BodyMBold>{fiberProLabels.yourOldPlan}</BodyMBold>
															</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														<TableRow>
															<TableCell>
																<BodySBold>
																	{changePlansDetails?.newPlan?.name}
																</BodySBold>
															</TableCell>
															<TableCell>
																<BodySBold>
																	{changePlansDetails?.oldPlan?.name}
																</BodySBold>
															</TableCell>
														</TableRow>
													</TableBody>
												</Table>
											</TableContainer>
										</div>
									</div>
								}
								hideDialog={() => {
									setConfirmationDialogOpen(false);
								}}
							/>
							<OtpDialog
								otp={otp}
								setOtp={setOtp}
								isOpen={isOTPStep}
								isWrongOtp={isWrongOtp}
								onSubmit={changeInternetPlan}
								onClose={() => setIsOtpStep(false)}
								heading={translate('number.settings.password.sent')}
								setIsWrongOtp={setIsWrongOtp}
								sendOtp={requestForOtp}
								loading={loading}
							/>
						</>
					)}
				/>
			}
		/>
	);
};

export default ChangePlanDialog;
